/** Tiny framework-free DOM + formatting helpers for the workspace UI. */
import { highlightCode } from "./highlight.js";


export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: Array<Node | string | null | undefined> = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else if (k === "html") node.innerHTML = v;
    else node.setAttribute(k, v);
  }
  for (const c of children) {
    if (c === null || c === undefined) continue;
    node.append(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

export function escapeHtml(s: string): string {
  // Quotes too: escaped text is interpolated into attributes (link hrefs),
  // and an unescaped quote there is an attribute breakout.
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * Safe markdown rendering: fenced code (with language label), inline code,
 * headings (h1–h3), bold, italic, links, blockquotes, ordered/unordered
 * lists, horizontal rules, and line breaks. No external deps.
 */
export function formatBody(raw: string): string {
  const parts = raw.split(/```/);
  return parts
    .map((part, i) => {
      if (i % 2 === 1) {
        const langMatch = part.match(/^([a-zA-Z0-9_+-]*)\n/);
        const lang = langMatch?.[1] ?? "";
        const raw = langMatch ? part.slice(langMatch[0].length) : part;
        const body = highlightCode(raw, lang);
        const label = lang ? `<span class="code-lang">${escapeHtml(lang)}</span>` : "";
        return `<pre class="code hljs">${label}${body}</pre>`;
      }
      return formatInline(part);
    })
    .join("");
}

function formatInline(raw: string): string {
  const lines = raw.split("\n");
  const out: string[] = [];
  let inList: "ul" | "ol" | null = null;
  let inBlockquote = false;

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li]!;

    // Blockquote
    if (/^>\s?/.test(line)) {
      const content = escapeHtml(line.replace(/^>\s?/, ""));
      if (!inBlockquote) { closeList(); out.push("<blockquote class=\"bq\">"); inBlockquote = true; }
      out.push(inlineFormat(content) + "<br>");
      continue;
    }
    if (inBlockquote) { out.push("</blockquote>"); inBlockquote = false; }

    // Horizontal rule
    if (/^[-*_]{3,}\s*$/.test(line.trim())) { closeList(); out.push("<hr class=\"hr\">"); continue; }

    // Headings
    const hMatch = line.match(/^(#{1,3})\s+(.+)/);
    if (hMatch) {
      closeList();
      const level = hMatch[1]!.length;
      out.push(`<h${level} class="md-h${level}">${inlineFormat(escapeHtml(hMatch[2]!))}</h${level}>`);
      continue;
    }

    // Unordered list
    if (/^[\s]*[-*+]\s+/.test(line)) {
      if (inList !== "ul") { closeList(); out.push("<ul class=\"md-ul\">"); inList = "ul"; }
      out.push(`<li>${inlineFormat(escapeHtml(line.replace(/^[\s]*[-*+]\s+/, "")))}</li>`);
      continue;
    }

    // Ordered list
    if (/^[\s]*\d+[.)]\s+/.test(line)) {
      if (inList !== "ol") { closeList(); out.push("<ol class=\"md-ol\">"); inList = "ol"; }
      out.push(`<li>${inlineFormat(escapeHtml(line.replace(/^[\s]*\d+[.)]\s+/, "")))}</li>`);
      continue;
    }

    closeList();
    // Tool activity the agent loop streams (desktop ipc toolCallLabel /
    // toolOutputBlock): "▸ `run.command …`…", "✓ `tool` — …", "✗ **tool** — …",
    // "✎ **path** — …". Rendered as compact activity rows, not prose, so the
    // reader can tell what the agent DID from what it SAID.
    const tool = /^([▸✓✗✎])\s+(.*)$/.exec(line);
    if (tool) {
      const kind = tool[1] === "▸" ? "run" : tool[1] === "✓" ? "ok" : tool[1] === "✗" ? "err" : "edit";
      out.push(`<div class="tool-line tool-${kind}"><span class="tool-mark" aria-hidden="true">${tool[1]!}</span><span class="tool-text">${inlineFormat(escapeHtml(tool[2]!))}</span></div>`);
      continue;
    }
    // Plain line
    if (line.trim() === "") { out.push("<br>"); continue; }
    out.push(inlineFormat(escapeHtml(line)) + "<br>");
  }

  closeList();
  if (inBlockquote) out.push("</blockquote>");
  return out.join("");

  function closeList(): void {
    if (inList === "ul") out.push("</ul>");
    else if (inList === "ol") out.push("</ol>");
    inList = null;
  }
}

function inlineFormat(escaped: string): string {
  return escaped
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
    // Only http(s), mailto and relative links become anchors. A model reply is
    // untrusted text (prompt injection can put anything in it), and a
    // `javascript:` or `data:` href would run in the app's own origin on click.
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text: string, href: string) =>
      safeHref(href) ? `<a href="${href}" target="_blank" rel="noopener noreferrer" class="md-link">${text}</a>` : `${text} (${href})`);
}

/** Is this (already HTML-escaped) link target safe to put in an href? */
export function safeHref(href: string): boolean {
  const h = href.trim().replace(/&amp;/g, "&");
  if (/^(https?:|mailto:)/i.test(h)) return true;
  // Relative: no scheme at all (a colon before any / ? # would be one).
  return !/^[^/?#]*:/.test(h) && !h.startsWith("//");
}

export function relTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function fmtUsd(n: number | null | undefined): string {
  if (n === null || n === undefined) return "$0.00";
  return n >= 1 || n === 0 ? `$${n.toFixed(2)}` : `$${n.toFixed(3)}`;
}

/** A transient toast in the corner, with an optional inline action button. */
export function toast(
  message: string,
  kind: "info" | "error" | "success" = "info",
  action?: { label: string; onClick: () => void },
): void {
  let host = document.getElementById("toasts");
  if (!host) {
    host = el("div", { id: "toasts", class: "toasts" });
    document.body.append(host);
  }
  const t = el("div", { class: `toast toast-${kind}` }, [el("span", { class: "toast-msg" }, [message])]);
  const dismiss = (): void => {
    t.classList.add("toast-out");
    setTimeout(() => t.remove(), 250);
  };
  if (action) {
    const btn = el("button", { class: "toast-action" }, [action.label]);
    btn.onclick = () => { dismiss(); action.onClick(); };
    t.append(btn);
  }
  host.append(t);
  // Actionable toasts linger a little longer so the button is clickable.
  setTimeout(dismiss, action ? 7000 : 3800);
}
